'use client'

import {
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
} from 'react'
import Link from 'next/link'
import {
  IconArrowRight,
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlug,
  IconPower,
  IconRefresh,
  IconServer,
  IconWallet,
} from '@tabler/icons-react'
import { cn } from '@/lib/utils'
import { u } from './power-builder'
import { MachineObject, MonitorObject, TowerObject, WalletObject, type ObjectProps } from './level-objects'

/*
 * A demonstration of the four steps. Nothing here calls an API: picking a
 * level only changes what is shown, so no account, payment or machine is
 * touched.
 */

interface Level {
  n: string
  title: string
  /** Outlined word behind the featured object. */
  code: string
  tagline: string
  status: string
  hue: string
  icon: typeof IconWallet
  body: string
  objectives: string[]
  stats: { value: string; label: string }[]
  Object: ComponentType<ObjectProps>
}

const hours = (n: number) => `${n} hour${n === 1 ? '' : 's'}`

// Timings come from lib/compliance.config.ts via the section, the same
// numbers the Terms promise, so no level can promise faster delivery.
function buildLevels(creditHours: number, provisioningMinutes: number, osNames: string[]): Level[] {
  const osList = osNames.join(' or ')
  return [
    {
      n: '01',
      title: 'Add credit',
      code: 'Credit',
      tagline: 'Power up your wallet',
      status: 'Wallet · ready to top up',
      hue: 'var(--primary)',
      icon: IconWallet,
      body: `Top up your wallet by card. Credit is added within ${hours(creditHours)} of payment, and there is no subscription.`,
      objectives: ['Top up your wallet by card', `Credit is added within ${hours(creditHours)} of payment`, 'No subscription to sign up for'],
      stats: [
        { value: `${creditHours} h`, label: 'Credit added within' },
        { value: 'None', label: 'Subscription' },
      ],
      Object: WalletObject,
    },
    {
      n: '02',
      title: 'Create a machine',
      code: 'Build',
      tagline: 'Assemble your loadout',
      status: 'Config · rate shown first',
      hue: 'var(--accent)',
      icon: IconServer,
      body: `Pick a GPU, a region and ${osList}. The form shows the exact hourly rate for that configuration before you create the machine.`,
      objectives: ['Select a GPU', 'Select a region', `Select ${osList}`, 'See the exact hourly rate before you create it'],
      stats: [
        { value: '3 picks', label: 'GPU, region and OS' },
        { value: 'Rate first', label: 'Hourly price shown up front' },
      ],
      Object: MachineObject,
    },
    {
      n: '03',
      title: 'Start your machine',
      code: 'Power',
      tagline: 'Power on',
      status: 'Power · booting',
      hue: 'var(--primary)',
      icon: IconPower,
      body: `Press Start and the machine boots. It is usually ready within ${provisioningMinutes} minutes. Billing starts when you start the machine, and start-up time is billed.`,
      objectives: ['Press Start on your machine', `Usually ready within ${provisioningMinutes} minutes`, 'Billing starts at Start, start-up time included'],
      stats: [
        { value: `${provisioningMinutes} min`, label: 'Usually ready within' },
        { value: 'At Start', label: 'Billing begins' },
      ],
      Object: TowerObject,
    },
    {
      n: '04',
      title: 'Connect & play',
      code: 'Connect',
      tagline: 'Full power',
      status: 'Link · connected',
      hue: 'var(--accent)',
      icon: IconPlug,
      body: 'Use Remote Desktop with the address and password on the machine page, or a compatible streaming client such as Parsec or Moonlight that you install yourself.',
      objectives: [
        'Open the machine page for the address and password',
        'Connect with Remote Desktop',
        'Or use a streaming client you install yourself, such as Parsec or Moonlight',
      ],
      stats: [
        { value: 'RDP', label: 'Remote Desktop' },
        { value: 'Your app', label: 'Parsec, Moonlight or similar' },
      ],
      Object: MonitorObject,
    },
  ]
}

const LAST = 3
const STEP_MS = 4600

const hueStyle = (hue: string) => ({ '--hue': hue }) as CSSProperties

function tilt(event: PointerEvent<HTMLDivElement>) {
  if (event.pointerType !== 'mouse' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  const el = event.currentTarget
  const rect = el.getBoundingClientRect()
  el.style.setProperty('--px', (((event.clientX - rect.left) / rect.width) * 2 - 1).toFixed(3))
  el.style.setProperty('--py', (((event.clientY - rect.top) / rect.height) * 2 - 1).toFixed(3))
}

function settle(event: PointerEvent<HTMLDivElement>) {
  event.currentTarget.style.setProperty('--px', '0')
  event.currentTarget.style.setProperty('--py', '0')
}

/** Pixel brackets framing the featured object. */
const CORNERS = [
  'left-[4%] top-[6%] border-l-2 border-t-2',
  'right-[4%] top-[6%] border-r-2 border-t-2',
  'left-[4%] bottom-[6%] border-b-2 border-l-2',
  'right-[4%] bottom-[6%] border-b-2 border-r-2',
]

export interface LevelSelectProps {
  creditHours: number
  provisioningMinutes: number
  osNames: string[]
  gpuHref: string
  className?: string
}

export function LevelSelect({ creditHours, provisioningMinutes, osNames, gpuHref, className }: LevelSelectProps) {
  const levels = buildLevels(creditHours, provisioningMinutes, osNames)
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const panelId = `${uid}-panel`

  const [active, setActive] = useState(0)
  const [playing, setPlaying] = useState(false)
  const tabs = useRef<(HTMLButtonElement | null)[]>([])

  const select = (index: number) => {
    setPlaying(false)
    setActive((index + levels.length) % levels.length)
  }

  // Autoplay walks the levels in order and stops on the last one.
  useEffect(() => {
    if (!playing) return
    const timer = window.setTimeout(() => {
      setActive(active + 1)
      if (active + 1 >= LAST) setPlaying(false)
    }, STEP_MS)
    return () => window.clearTimeout(timer)
  }, [playing, active])

  const togglePlay = () => {
    if (playing) {
      setPlaying(false)
      return
    }
    if (active >= LAST) setActive(0)
    setPlaying(true)
  }

  const onTabKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    const targets: Record<string, number> = {
      ArrowRight: active + 1,
      ArrowDown: active + 1,
      ArrowLeft: active - 1,
      ArrowUp: active - 1,
      Home: 0,
      End: LAST,
    }
    if (!(event.key in targets)) return
    event.preventDefault()
    const next = (targets[event.key] + levels.length) % levels.length
    select(next)
    tabs.current[next]?.focus()
  }

  const level = levels[active]
  const LevelIcon = level.icon
  const percent = ((active + 1) / levels.length) * 100
  const atEnd = active >= LAST

  return (
    <div className={cn('grid gap-4 lg:grid-cols-12 lg:gap-5', className)}>
      {/* ------------------------------------------------ featured stage */}
      <div
        onPointerMove={tilt}
        onPointerLeave={settle}
        className="lvs-stage relative flex flex-col overflow-hidden rounded-[2rem] lg:col-span-7"
        style={hueStyle(level.hue)}
      >
        <div className="relative z-10 flex flex-wrap items-center justify-between gap-2 px-4 pt-4 sm:px-6 sm:pt-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            World 1 · Zero to cloud ·{' '}
            <span className="font-bold text-foreground">
              Lv <span className="lvs-hue-text">{level.n}</span>/0{levels.length}
            </span>
          </p>
          <span
            key={`status-${active}`}
            className="lvs-fade inline-flex items-center gap-2 rounded-full bg-background/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-foreground ring-1 ring-foreground/15"
          >
            <span aria-hidden className="lab-led-on power-pulse h-1.5 w-1.5 rounded-full" />
            {level.status}
          </span>
        </div>

        <div aria-hidden className="relative h-[calc(var(--u)*430)] [--u:0.58px] sm:[--u:0.78px] lg:[--u:0.68px] xl:[--u:0.84px]">
          <p
            key={`code-${active}`}
            className="select-outline-type lvs-fade absolute inset-x-0 top-[4%] whitespace-nowrap text-center font-heading font-bold uppercase leading-none"
            style={{ fontSize: u(118) }}
          >
            {level.code}
          </p>
          <span className="lvs-horizon absolute inset-x-[10%] top-[82%] h-px" />
          {CORNERS.map((corner) => (
            <span key={corner} className={cn('lvs-corner absolute h-4 w-4', corner)} />
          ))}

          <div key={`scene-${active}`} className="lvs-fade absolute inset-0 [perspective:1100px]">
            <div
              className="absolute left-1/2 top-1/2 [transform-style:preserve-3d] transition-transform duration-500 ease-out motion-reduce:transition-none"
              style={{ transform: 'rotateX(calc(-16deg + var(--py, 0) * -8deg)) rotateY(calc(-28deg + var(--px, 0) * 24deg))' }}
            >
              <div className="lvs-spin-in [transform-style:preserve-3d]">
                <div className="lab-sway [transform-style:preserve-3d]">
                  <level.Object live />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* control bar */}
        <div className="relative z-10 mt-auto flex flex-wrap items-center gap-x-5 gap-y-3 border-t border-foreground/10 bg-background/45 px-4 py-3.5 sm:px-6">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => select(active - 1)}
              aria-label="Previous level"
              className="lvs-btn grid h-10 w-10 place-items-center rounded-xl text-foreground"
            >
              <IconChevronLeft aria-hidden className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={togglePlay}
              aria-pressed={playing}
              aria-label={playing ? 'Pause the tour' : atEnd ? 'Replay the tour' : 'Play the tour'}
              className="lvs-btn inline-flex h-10 items-center gap-2 rounded-xl px-3.5 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-foreground"
            >
              {playing ? (
                <IconPlayerPause aria-hidden className="lvs-hue-text h-4 w-4" />
              ) : atEnd ? (
                <IconRefresh aria-hidden className="lvs-hue-text h-4 w-4" />
              ) : (
                <IconPlayerPlay aria-hidden className="lvs-hue-text h-4 w-4" />
              )}
              {playing ? 'Pause' : atEnd ? 'Replay' : 'Play'}
            </button>
            <button
              type="button"
              onClick={() => select(active + 1)}
              aria-label="Next level"
              className="lvs-btn grid h-10 w-10 place-items-center rounded-xl text-foreground"
            >
              <IconChevronRight aria-hidden className="h-4 w-4" />
            </button>
          </div>

          <div className="min-w-[10rem] flex-1">
            <div className="flex items-baseline justify-between font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              <span>Cloud sync</span>
              <span className="lvs-hue-text font-bold tabular-nums">{percent}%</span>
            </div>
            <div aria-hidden className="mt-1.5 grid grid-cols-4 gap-1.5">
              {levels.map((lv, i) => (
                <span key={lv.n} className="lvs-seg h-2 overflow-hidden rounded-[3px]">
                  {i < active && <span className="block h-full w-full" />}
                  {i === active && (
                    <span
                      key={`${active}-${playing}`}
                      className={cn('block h-full w-full', playing && 'lvs-fill-run')}
                      style={{ '--step': `${STEP_MS}ms` } as CSSProperties}
                    />
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
        <p aria-live="polite" className="sr-only">
          Level {level.n} of 0{levels.length}: {level.title}
        </p>
      </div>

      {/* ------------------------------------------------- level rail */}
      <div role="tablist" aria-label="Levels" className="grid grid-cols-4 gap-2 sm:gap-3 lg:order-last lg:col-span-12">
        {levels.map((lv, i) => {
          const on = i === active
          const Icon = lv.icon
          return (
            <button
              key={lv.n}
              ref={(el) => {
                tabs.current[i] = el
              }}
              type="button"
              role="tab"
              id={`${uid}-tab-${i}`}
              aria-selected={on}
              aria-controls={panelId}
              aria-label={`Level ${lv.n}: ${lv.title}`}
              tabIndex={on ? 0 : -1}
              onClick={() => select(i)}
              onKeyDown={onTabKey}
              className={cn(
                'lvs-slot group relative flex flex-col overflow-hidden rounded-2xl text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                on && 'lvs-slot-on',
              )}
              style={hueStyle(lv.hue)}
            >
              <span aria-hidden className="relative hidden h-24 [--u:0.2px] sm:block lg:h-28 lg:[--u:0.24px]">
                <span className="lvs-thumb absolute inset-0">
                  <span className="absolute inset-0 [perspective:700px]">
                    <span
                      className="absolute left-1/2 top-1/2 [transform-style:preserve-3d]"
                      style={{ transform: 'rotateX(-16deg) rotateY(-28deg)' }}
                    >
                      <lv.Object live={false} />
                    </span>
                  </span>
                </span>
                {on && (
                  <span className="absolute right-2.5 top-2.5 inline-flex items-center gap-1.5 rounded-full bg-background/75 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-foreground ring-1 ring-foreground/15">
                    <span className="lab-led-on power-pulse h-1.5 w-1.5 rounded-full" />
                    Now
                  </span>
                )}
              </span>
              <span aria-hidden className="flex items-center gap-2 p-2.5 sm:gap-3 sm:border-t sm:border-foreground/10 sm:p-3">
                <span
                  className={cn(
                    'grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors',
                    on ? 'lvs-hue-bg text-primary-foreground' : 'bg-background/70 text-foreground ring-1 ring-foreground/15',
                  )}
                >
                  <Icon className="h-4.5 w-4.5" stroke={2} />
                </span>
                <span className="min-w-0">
                  <span className="block font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                    Lv {lv.n}
                    {i < active && <IconCheck className="lvs-hue-text ml-1 inline h-3 w-3 align-[-1px]" stroke={3} />}
                  </span>
                  <span className="hidden truncate font-heading text-sm font-bold text-foreground sm:block">{lv.title}</span>
                </span>
              </span>
            </button>
          )
        })}
      </div>

      {/* ------------------------------------------------- briefing */}
      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={`${uid}-tab-${active}`}
        tabIndex={0}
        className="lvs-panel relative flex flex-col rounded-[2rem] p-5 outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-7 lg:col-span-5"
        style={hueStyle(level.hue)}
      >
        <div key={active} className="flex flex-1 flex-col">
          <div className="lvs-reveal flex flex-wrap items-center gap-2">
            <span className="lvs-hue-bg inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 font-mono text-xs font-bold uppercase tracking-wider text-primary-foreground">
              <LevelIcon aria-hidden className="h-3.5 w-3.5" stroke={2.4} />
              Level {level.n}
            </span>
            <span className="lvs-hue-text font-mono text-xs uppercase tracking-[0.18em]">{level.tagline}</span>
          </div>
          <h3
            className="lvs-reveal mt-4 font-heading text-3xl font-bold uppercase leading-[0.95] tracking-tight text-foreground md:text-4xl"
            style={{ animationDelay: '0.05s' }}
          >
            {level.title}
          </h3>
          <p className="lvs-reveal mt-3 text-base leading-relaxed text-foreground/80" style={{ animationDelay: '0.1s' }}>
            {level.body}
          </p>

          <ul className="mt-5 space-y-2.5">
            {level.objectives.map((objective, k) => (
              <li
                key={objective}
                className="lvs-reveal flex gap-3 text-sm leading-snug text-foreground/90"
                style={{ animationDelay: `${0.14 + 0.06 * k}s` }}
              >
                <span aria-hidden className="lvs-hue-bg mt-px grid h-4.5 w-4.5 shrink-0 place-items-center rounded-[4px] text-primary-foreground">
                  <IconCheck className="h-3 w-3" stroke={3} />
                </span>
                {objective}
              </li>
            ))}
          </ul>

          <dl className="mt-6 grid grid-cols-2 gap-3">
            {level.stats.map((stat, k) => (
              <div
                key={stat.label}
                className="lvs-reveal rounded-2xl bg-background/55 px-4 py-3 ring-1 ring-foreground/10"
                style={{ animationDelay: `${0.3 + 0.06 * k}s` }}
              >
                <dt className="text-xs text-muted-foreground">{stat.label}</dt>
                <dd className="lvs-hue-text mt-0.5 font-heading text-2xl font-bold tabular-nums">{stat.value}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-6">
            {active < LAST ? (
              <button
                type="button"
                onClick={() => select(active + 1)}
                className="inline-flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wide text-foreground transition-colors hover:text-[color:var(--hue)]"
              >
                Next: {levels[active + 1].title}
                <IconArrowRight aria-hidden className="h-4 w-4" />
              </button>
            ) : (
              <Link
                href={gpuHref}
                className="arcade-press inline-flex h-12 items-center gap-2 rounded-xl bg-primary px-6 font-heading text-sm font-bold uppercase tracking-wide text-primary-foreground"
              >
                Pick a GPU
                <IconArrowRight aria-hidden className="h-4 w-4" />
              </Link>
            )}
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Step {active + 1} of {levels.length}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
